import { useMemo } from 'react';

import { useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';
import { useServerEvents } from '#lib/useServerEvents';

import {
	fetchCompanyDetail,
	fetchCompanyFinancialHistory,
	fetchCompanyEvents,
	fetchCompanyContacts,
	fetchCompanyDirectors,
} from '../../api/companyDetailApi';
import { fetchLeadsBySearchQueryId } from '../../api/leadApi';

import { companiesKeys } from './keys';

export const companiesByProspectQueryIdOptions = (
	prospectQueryId,
	{ page = 1, pageSize = 100, orderBy = 'created-at', orderDir = 'desc', filters = {} } = {},
) =>
	queryOptions({
		queryKey: companiesKeys.byProspectQuery(prospectQueryId, { page, pageSize, orderBy, orderDir, filters }),
		queryFn: async () => {
			const result = await fetchLeadsBySearchQueryId(prospectQueryId, {
				page,
				pageSize,
				orderBy,
				orderDir,
				filters,
			});
			if (result.error) throw result.error;
			return result;
		},
		staleTime: STALE_TIMES.STANDARD,
	});

export function useCompaniesByProspectQueryIdWithRealtime(
	prospectQueryId,
	{ page = 1, pageSize = 100, orderBy = 'created-at', orderDir = 'desc', filters = {} } = {},
	isJobRunning = false,
) {
	const queryClient = useQueryClient();
	const query = useQuery({
		...companiesByProspectQueryIdOptions(prospectQueryId, { page, pageSize, orderBy, orderDir, filters }),
		enabled: Boolean(prospectQueryId),
	});

	const topics = useMemo(
		() => (prospectQueryId && isJobRunning ? [`leads:${prospectQueryId}`] : []),
		[prospectQueryId, isJobRunning],
	);

	useServerEvents(topics, {
		enabled: Boolean(prospectQueryId) && isJobRunning,
		onEvent: () => {
			// Prefix invalidation: during a scrape every page/sort/filter view of this
			// query is stale, and it cannot drift from the exact key shape above.
			queryClient.invalidateQueries({ queryKey: companiesKeys.byProspectQueryBase(prospectQueryId) });
		},
	});

	return query;
}

// Company Detail Page queries

export function useCompanyDetail(globalCompanyId) {
	return useQuery({
		queryKey: companiesKeys.detail(globalCompanyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyDetail(globalCompanyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(globalCompanyId),
	});
}

export function useCompanyFinancialHistory(globalCompanyId) {
	return useQuery({
		queryKey: companiesKeys.financialHistory(globalCompanyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyFinancialHistory(globalCompanyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(globalCompanyId),
	});
}

export function useCompanyEvents(globalCompanyId) {
	return useQuery({
		queryKey: companiesKeys.events(globalCompanyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyEvents(globalCompanyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(globalCompanyId),
	});
}

export function useCompanyContacts(globalCompanyId) {
	return useQuery({
		queryKey: companiesKeys.contacts(globalCompanyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyContacts(globalCompanyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STANDARD,
		enabled: Boolean(globalCompanyId),
	});
}

export function useCompanyDirectors(globalCompanyId) {
	return useQuery({
		queryKey: companiesKeys.directors(globalCompanyId),
		queryFn: async () => {
			const { data, error } = await fetchCompanyDirectors(globalCompanyId);
			if (error) throw error;
			return data;
		},
		staleTime: STALE_TIMES.STABLE,
		enabled: Boolean(globalCompanyId),
	});
}
